// StatCard component for UI Component Library
// Created as part of ENG2-1691: Phase 3 - Ticket 3.2

import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons'
import { tokens } from '../../../styles/tokens'
import { StatCardProps, IconVariant } from './stat-card.types'

// Icon color configurations
const iconColors: Record<IconVariant, { bg: string; color: string }> = {
  blue: {
    bg: tokens.colors.info[100],
    color: tokens.colors.info[500],
  },
  green: {
    bg: tokens.colors.success[100],
    color: tokens.colors.success[500],
  },
  orange: {
    bg: tokens.colors.accent[100],
    color: tokens.colors.accent[500],
  },
  red: {
    bg: tokens.colors.error[100],
    color: tokens.colors.error[500],
  },
}

// Styled card container
const CardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: tokens.radius.lg,
  padding: '22px 24px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
}))

// Styled icon container
const IconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'iconVariant',
})<{ iconVariant: IconVariant }>(({ iconVariant }) => ({
  width: 38,
  height: 38,
  borderRadius: tokens.radius.sm,
  backgroundColor: iconColors[iconVariant].bg,
  color: iconColors[iconVariant].color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: 16,
}))

// Main StatCard component
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  iconVariant,
  change,
  sx,
  className,
}) => {
  return (
    <CardContainer sx={sx} className={className}>
      <IconContainer iconVariant={iconVariant}>{icon}</IconContainer>
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontSize: 13,
            color: 'text.secondary',
            marginBottom: 0.5,
          }}
        >
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 700,
              fontFamily: '"Montserrat", sans-serif',
              color: 'text.primary',
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>
          {change && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color:
                  change.direction === 'up'
                    ? tokens.colors.success[600]
                    : tokens.colors.error[600],
              }}
            >
              <FontAwesomeIcon
                icon={change.direction === 'up' ? faArrowUp : faArrowDown}
                style={{ fontSize: 10 }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                {change.text}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </CardContainer>
  )
}

StatCard.displayName = 'StatCard'

export default StatCard
export type { StatCardProps, StatChange, IconVariant } from './stat-card.types'
